import React from 'react';
import { ArrowDownIcon, ArrowUpDownIcon, SparklesIcon, RefreshCwIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import type { Bi } from '../../data/sectors';
import { overviewMap as c, LOGO_BCITI, LOGO_ATNA } from '../../data/platformOverview';
import { ENTRY_ICONS, CORE_ICONS, DEST_ICONS } from './mapParts';
function Label({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-center font-sans text-[11px] font-semibold tracking-[0.22em] uppercase text-[#121A3A]" data-id="element-5536" data-mp="zmwwspetar7v">{children}</p>;
}
function Connector({
  both
}: {
  both?: boolean;
}) {
  const Icon = both ? ArrowUpDownIcon : ArrowDownIcon;
  return <div className="flex flex-col items-center py-4" aria-hidden="true" data-id="element-5537" data-mp="19cq6fkve8w9h">
      <span className="w-px h-5 bg-[#CFCBF5]" data-id="element-5538" data-mp="1e9q4p9m04uq1" />
      <Icon className="w-3.5 h-3.5 text-[#8B85F0]" data-id="element-5539" data-mp="1ja4eaw394cq6" />
    </div>;
}

// Vertical version of the same architecture for small screens.
export function EcosystemMapMobile() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <div className="max-w-[520px] mx-auto" data-id="element-5540" data-mp="38444ioby8q6c">
      <Label data-id="element-5541" data-mp="3336nwg6l846y">{pick(c.entryLabel)}</Label>
      <p className="mt-1 text-center font-sans text-[13px] text-[#7A8096]" data-id="element-5542" data-mp="33250dc8dxp8c">{pick(c.entrySub)}</p>
      <ul className="mt-5 flex flex-wrap justify-center gap-y-3" data-id="element-5543" data-mp="2y5541t1c80uf">
        {c.entries.map(e => {
        const Icon = ENTRY_ICONS[e.key];
        return <li key={e.key} className="w-1/4 px-1 flex flex-col items-center text-center" data-id="element-5544" data-mp="3qttvt1gi6tti">
              <span className="w-10 h-10 rounded-xl bg-white border border-[#E7E6F3] flex items-center justify-center" data-id="element-5545" data-mp="3lto0nbb0yp6o">
                <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.6} data-id="element-5546" data-mp="3h2frmgdlx5u1" />
              </span>
              <span className="mt-1.5 font-sans text-[12.5px] font-medium leading-tight text-[#121A3A]" data-id="element-5547" data-mp="3c3mo9gs1av7l">{pick(e.label)}</span>
            </li>;
      })}
      </ul>

      <Connector data-id="element-5548" data-mp="8n5rhdta2aow" />

      <div className="rounded-[28px] bg-[#F4F3FE] p-5" data-id="element-5549" data-mp="8mml8uczpaqp">
        <div className="flex flex-col items-center" data-id="element-5550" data-mp="22vjdrzflyv2n">
          <SparklesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} data-id="element-5551" data-mp="22wuko84coixz" />
          <div className="mt-1 flex items-center" aria-label="BCITI | ATNA" data-id="element-5552" data-mp="27uw2yqtyce8e">
            <img src={LOGO_BCITI} alt="BCITI" className="h-10 w-auto object-contain" data-id="element-5553" data-mp="1t5zrskwlml3k" />
            <span className="mx-1 text-[#121A3A]/25 text-lg font-light select-none" aria-hidden="true" data-id="element-5554" data-mp="1y4t7mjzcyo6w">|</span>
            <img src={LOGO_ATNA} alt="ATNA" className="h-9 w-auto object-contain" data-id="element-5555" data-mp="1k5a0nuia9iw6" />
          </div>
          <p className="mt-1 font-sans text-[11px] font-semibold tracking-[0.22em] uppercase text-[#4F46E5] text-center" data-id="element-5556" data-mp="1o6dk0qzjhx25">{pick(c.coreLabel)}</p>
        </div>
        <ul className="mt-5 flex flex-wrap justify-center gap-2" data-id="element-5557" data-mp="1a8ou34qm7zq8">
          {c.core.map(n => {
          const Icon = CORE_ICONS[n.key];
          return <li key={n.key} className="flex items-center gap-1.5 rounded-full bg-white border border-[#D8D5F7] pl-1 pr-3 py-1" data-id="element-5558" data-mp="34lwurd19v5js">
                <span className="w-6 h-6 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-5559" data-mp="39j6rzeuwkli6">
                  <Icon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-5560" data-mp="xwwc6sya3w38" />
                </span>
                <span className="font-sans text-[13px] font-semibold text-[#121A3A]" data-id="element-5561" data-mp="svfawwbvu7m4">{pick(n.label)}</span>
              </li>;
        })}
        </ul>
      </div>

      <Connector both data-id="element-5562" data-mp="12uxuhbnvrrdn" />

      <Label data-id="element-5563" data-mp="12tmpj8ip8dky">{pick(c.servicesLabel)}</Label>
      <ul className="mt-3 flex flex-wrap justify-center gap-2" data-id="element-5564" data-mp="1gmkxducbvy5e">
        {c.services.map(s => <li key={s.fr} className="flex items-center gap-1.5 rounded-full bg-white border border-[#E4E2F6] px-3 py-1.5" data-id="element-5565" data-mp="1bosxoe5fz7qn">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" data-id="element-5566" data-mp="1qjz6f8vx9kno" />
            <span className="font-sans text-[13px] font-medium text-[#2A3150]" data-id="element-5567" data-mp="1llnwyz9jhc5z">{pick(s)}</span>
          </li>)}
      </ul>

      <div className="mt-7" data-id="element-5568" data-mp="3sdgak2kug3do">
        <Label data-id="element-5569" data-mp="3serf44m45f5u">{pick(c.ecosystemLabel)}</Label>
        <ul className="mt-3 flex flex-wrap justify-center gap-2" data-id="element-5570" data-mp="3nec2xgdkidpg">
          {c.ecosystem.map(s => <li key={s.fr} className="flex items-center gap-1.5 rounded-full bg-[#FBFBFD] border border-[#ECEDF2] px-3 py-1.5" data-id="element-5571" data-mp="2efkbsbj22do3">
              <span className="w-1.5 h-1.5 rounded-full border border-[#9AA0B4]" data-id="element-5572" data-mp="2jdvlmm9coo6b" />
              <span className="font-sans text-[13px] text-[#5A6178]" data-id="element-5573" data-mp="2og4frlxylwa0">{pick(s)}</span>
            </li>)}
        </ul>
        <p className="mt-4 text-center font-serif text-[16px] leading-snug text-[#4A5268]" data-id="element-5574" data-mp="2seakkof8m1sa">
          {c.ecosystemNote.map(l => <span key={l.fr} className="block" data-id="element-5575" data-mp="20oz0p8zuf80r">{pick(l)}</span>)}
        </p>
      </div>

      <Connector data-id="element-5576" data-mp="24r2fa6ht6sq9" />

      <Label data-id="element-5577" data-mp="24qk84etlyu3k">{pick(c.destinationsLabel)}</Label>
      <ul className="mt-3 grid grid-cols-3 gap-2" data-id="element-5578" data-mp="24opp561wdc24">
        {c.destinations.map(d => {
        const Icon = DEST_ICONS[d.key];
        return <li key={d.key} className="flex flex-col items-center text-center rounded-2xl bg-white border border-[#ECEBF5] px-2 py-3" data-id="element-5579" data-mp="1cyl7vg9fp554">
              <span className="w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-5580" data-mp="1gzeyihe9gy1p">
                <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} data-id="element-5581" data-mp="19gxg1loeq8wl" />
              </span>
              <span className="mt-2 font-sans text-[12.5px] font-semibold leading-tight text-[#121A3A]" data-id="element-5582" data-mp="14jf4dnj09fkw">{pick(d.label)}</span>
            </li>;
      })}
      </ul>

      <p className="mt-6 flex items-center justify-center gap-2 font-sans text-[13px] italic text-[#4F46E5]" data-id="element-5583" data-mp="vh5wz6j1a2bq">
        <RefreshCwIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-5584" data-mp="qkfiuoqshgsy" />
        {pick(c.loop)}
      </p>
    </div>;
}